import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useStore } from '../../store/useStore';
import { Pickaxe, Wheat, Hammer, PartyPopper, Lock, Unlock } from 'lucide-react';
import { MINING_DATA } from '../../data/mining';
import { FARMING_DATA } from '../../data/farming';
import { CRAFTING_RECIPES } from '../../data/crafting';

const SKILL_ICONS: Record<string, React.ReactNode> = {
    mining: <Pickaxe size={48} color="#f97316" />,
    farming: <Wheat size={48} color="#10b981" />,
    crafting: <Hammer size={48} color="#eab308" />,
};

interface LevelUp {
    skillId: string;
    level: number;
}

interface UnlockedItem {
    name: string;
    type: string;
}

// Farming plots unlock at these levels (shared with FarmingPanel)
const PLOT_UNLOCK_LEVELS: { level: number; plotNumber: number }[] = [
    { level: 20, plotNumber: 2 },
    { level: 80, plotNumber: 3 },
];

function getUnlocksForLevel(skillId: string, level: number): UnlockedItem[] {
    const unlocks: UnlockedItem[] = [];

    if (skillId === 'mining') {
        MINING_DATA.filter(ore => ore.level === level).forEach(ore => {
            unlocks.push({ name: ore.name, type: 'Ore' });
        });
    } else if (skillId === 'farming') {
        FARMING_DATA.filter(crop => crop.level === level).forEach(crop => {
            unlocks.push({ name: crop.name, type: 'Crop' });
        });
        // Check for farming plot unlocks
        const plotUnlock = PLOT_UNLOCK_LEVELS.find(p => p.level === level);
        if (plotUnlock) {
            unlocks.push({ name: `Farming Plot ${plotUnlock.plotNumber}`, type: 'Upgrade' });
        }
    } else if (skillId === 'crafting') {
        CRAFTING_RECIPES.filter(recipe => recipe.level === level).forEach(recipe => {
            unlocks.push({ name: recipe.name, type: recipe.category.charAt(0).toUpperCase() + recipe.category.slice(1) });
        });
    }

    return unlocks;
}

export const LevelingNotification: React.FC = () => {
    const skills = useStore((state) => state.skills);
    const [prevSkills, setPrevSkills] = useState(skills);
    const [queue, setQueue] = useState<LevelUp[]>([]);
    const [active, setActive] = useState<LevelUp | null>(null);
    const canCloseRef = useRef(false);

    const dismiss = useCallback(() => {
        if (canCloseRef.current) {
            setActive(null);
        }
    }, []);

    useEffect(() => {
        Object.keys(skills).forEach((key) => {
            const skillId = key;
            const current = skills[skillId];
            const previous = prevSkills?.[skillId];

            if (current && previous && current.level > previous.level) {
                setQueue((q) => [...q, { skillId, level: current.level }]);
            }
        });
        setPrevSkills(skills);
    }, [skills]);

    useEffect(() => {
        if (!active && queue.length > 0) {
            const next = queue[0];
            if (next) {
                setQueue((q) => q.slice(1));
                setActive(next);
            }
        }
    }, [active, queue]);

    // Separate effect for the close delay - runs when active becomes truthy
    useEffect(() => {
        if (active) {
            canCloseRef.current = false;

            const closeTimer = setTimeout(() => {
                canCloseRef.current = true;
            }, 1000);

            return () => {
                clearTimeout(closeTimer);
            };
        }
    }, [active]);

    const unlockedItems = useMemo(() => {
        if (!active) return [];
        return getUnlocksForLevel(active.skillId, active.level);
    }, [active]);

    if (!active) return null;

    const skillName = active.skillId.charAt(0).toUpperCase() + active.skillId.slice(1);
    const icon = SKILL_ICONS[active.skillId] || <PartyPopper size={48} color="#a855f7" />;

    return (
        <div className="level-up-toast-overlay" onClick={dismiss}>
            <div className="level-up-toast" onClick={(e) => e.stopPropagation()}>
                <div className="level-up-content">
                    <div className="level-up-decoration">
                        <PartyPopper size={64} color="#facc15" />
                    </div>

                    <div className="level-up-icon-wrapper">
                        {icon}
                    </div>

                    <div className="level-up-heading">
                        <h2 className="level-up-title">
                            {skillName} Level Up!
                        </h2>
                        <p className="level-up-text">
                            You reached level <span className="level-up-level">{active.level}</span>
                        </p>
                    </div>

                    {unlockedItems.length > 0 && (
                        <div className="level-up-unlocks">
                            <div className="level-up-unlocks-header">
                                <Unlock size={16} color="#22c55e" />
                                <span>New Unlocks!</span>
                            </div>
                            <ul className="level-up-unlocks-list">
                                {unlockedItems.map((item, i) => (
                                    <li key={i} className="level-up-unlock-item">
                                        <span className="level-up-unlock-name">{item.name}</span>
                                        <span className="level-up-unlock-type">{item.type}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <p className="level-up-dismiss-hint">Click anywhere to close</p>
                </div>
            </div>
        </div>
    );
};
